'use client'

import {useEffect, useRef, useCallback, useState} from 'react'
import {tv, type VariantProps} from 'tailwind-variants'
import {CHROMATIC_IGNORE_CLASSNAME} from '~/utils/chromatic'

const hgTextRevealVariants = tv({
  slots: {
    container: 'flex h-full items-center text-text-default',
    words: 'flex flex-wrap',
  },
  variants: {
    size: {
      large: {
        words: 'arcadia-heading-1',
      },
      small: {
        words: 'arcadia-heading-3',
      },
    },
    alignment: {
      left: {
        container: 'justify-start',
        words: 'justify-start',
      },
      center: {
        container: 'justify-center',
        words: 'justify-center',
      },
    },
  },
})

export type HgTextRevealProps = VariantProps<typeof hgTextRevealVariants> & {
  text: string
}

export default function HgTextReveal({
  text,
  size = 'large',
  alignment = 'center',
}: HgTextRevealProps) {
  const targetRef = useRef<HTMLDivElement | null>(null)
  const [scrollYProgress, setScrollYProgress] = useState(0)

  const {container, words: wordsVariant} = hgTextRevealVariants({size, alignment})

  const words = text.split(' ')

  const handleScroll = useCallback(() => {
    if (!targetRef.current) {
      return
    }

    const rect = targetRef.current.getBoundingClientRect()
    const elementHeight = rect.height

    const midpoint = window.innerHeight / 2
    const elementMidpoint = rect.top + elementHeight / 2

    // Reveal animation starts when element midpoint is at viewport bottom
    const start = window.innerHeight - elementMidpoint

    // Reveal animation ends when element midpoint reaches viewport midpoint
    const end = midpoint - elementMidpoint

    const progress = Math.min(Math.max(start / (start - end), 0), 1)
    setScrollYProgress(progress)
  }, [])

  useEffect(() => {
    handleScroll()
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [handleScroll])

  return (
    <div ref={targetRef} className={CHROMATIC_IGNORE_CLASSNAME}>
      <div className={container()}>
        <span className={wordsVariant()}>
          <span className="sr-only">{text}</span>
          {words.map((word, i) => {
            const start = i / words.length
            const end = (i + 1) / words.length
            const opacity =
              0.25 +
              0.75 *
                Math.min(Math.max((scrollYProgress - start) / (end - start), 0), 1)

            return (
              <span
                key={i}
                aria-hidden="true"
                className="opacity-[var(--opacity)] transition-opacity duration-300"
                style={
                  {
                    '--opacity': opacity,
                  } as React.CSSProperties
                }
              >
                {word}
                {i < words.length - 1 ? <>&nbsp;</> : ''}
              </span>
            )
          })}
        </span>
      </div>
    </div>
  )
}
